import { Outlet, useLocation } from 'react-router-dom'
import { useState, useEffect } from 'react'
import PaneErrorBoundary from '../PaneErrorBoundary'
import Sidebar from './Sidebar'
import MobileLayout from './MobileLayout'
import OfflineStatusBanner from './OfflineStatusBanner'
import JanetFAB from '../janet/JanetFAB'
import EventNudge from './EventNudge'
import BugReportFAB from '../bugReport/BugReportFAB'
import InboundCallPopup from '../calls/InboundCallPopup'
import MondrianBg from '../MondrianBg'
import MotherwellBg from '../MotherwellBg'
import BlockTabBar from './BlockTabBar'
import WorkspaceTabStrip from './WorkspaceTabStrip'
import JanetReturnBridge from './JanetReturnBridge'
import UniversalBar from './UniversalBar'
import DemoRail from '../demo/DemoRail'
import useIsMobile from '../../hooks/useIsMobile'
import useDeploymentRefresh from '../../hooks/useDeploymentRefresh'
import { useAuth } from '../../contexts/AuthContext'
import { useUserProfile } from '../../hooks/useUserProfile'
import { ThemeProvider, useTheme } from '../../contexts/ThemeContext'
import ThemeWrapper from '../ThemeWrapper'
import { ROTHKO_MODULES, MONDRIAN_MODULES, BLOCKS } from '../../config/themes'
import { useToast } from '../../contexts/ToastContext'
import { useCallLogReview } from '../../hooks/useCallLogReview'
import CallLogReviewModal from '../comms/CallLogReviewModal'
import { MessagesUnreadProvider } from '../../contexts/MessagesUnreadContext'
import { useJanetDesktopNotifications } from '../../hooks/useJanetDesktopNotifications'
import { useJanetPage } from '../../contexts/JanetPageContext'
import ShellFrame from '../shell/ShellFrame'
import useShellPreview from '../shell/shellPreview'

const SENTIMENT_TYPE = { warm: 'success', neutral: 'info', cool: 'info', tense: 'warning' }

// Inner shell — can read useTheme() since it renders inside ThemeProvider
function DesktopShell({ collapsed, toggleCollapse, setSidebarOpen, updateReady, applyUpdate }) {
  const { theme, module, block } = useTheme()
  const location = useLocation()
  const toast = useToast()
  const { compactDockExpanded } = useJanetPage()
  // Shared-shell review pass (Aaron, 2026-09-30): opt-in via ?shell=preview.
  const shellPreview = useShellPreview()
  const { markReviewed } = useCallLogReview()
  const [reviewQueue, setReviewQueue] = useState([])
  const activeReview = reviewQueue[0] || null

  // Show post-call Janet summary as a toast for manually-logged calls (confirmed immediately).
  // For RC/Granola calls (needsReview=true), open the review modal instead.
  useEffect(() => {
    function handleCallLogged(e) {
      const { summary, sentiment, callLogId, draftId, needsReview } = e.detail || {}
      if (needsReview) return // handled by useCallLogReview poll + janet:call-review-needed
      if (!summary) return
      const type = SENTIMENT_TYPE[sentiment] || 'info'
      const draftHint = draftId ? ' — draft follow-up queued in Janet.' : ''
      toast[type](`Janet: ${summary}${draftHint}`, 10000)
    }
    window.addEventListener('janet:call-logged', handleCallLogged)
    return () => window.removeEventListener('janet:call-logged', handleCallLogged)
  }, [toast])

  // Open review modal when a pending_review row is detected by the poll hook.
  // Only auto-open for FRESH rows (call/meeting processed in the last 15 min) —
  // the backlog lives in the Comms "To Review" queue and must not spawn a modal
  // on every page load.
  useEffect(() => {
    function handleReviewNeeded(e) {
      const callLog = e.detail
      if (!callLog) return
      const ageMs = callLog.created_at ? Date.now() - new Date(callLog.created_at).getTime() : Infinity
      if (ageMs > 15 * 60 * 1000) return
      setReviewQueue(prev => prev.some(r => r.id === callLog.id) ? prev : [...prev, callLog])
    }
    window.addEventListener('janet:call-review-needed', handleReviewNeeded)
    return () => window.removeEventListener('janet:call-review-needed', handleReviewNeeded)
  }, [])

  function handleConfirmed(callLogId) {
    markReviewed(callLogId)
    setReviewQueue(prev => prev.filter(r => r.id !== callLogId))
  }

  function handleDismiss() {
    // One click gets you back into the OS: close the popup entirely.
    // Everything unreviewed stays in the Comms "To Review" queue.
    setReviewQueue([])
  }

  const outerStyle = (() => {
    if (theme === 'blocks') {
      // Page = mid tint of the active Block (derived in CSS from --module-accent)
      return { background: 'var(--block-body-bg, #F6F5F1)', transition: 'background 0.35s ease' }
    }
    if (theme === 'rothko') {
      const r = ROTHKO_MODULES[module] || ROTHKO_MODULES.default
      return { background: r.bg, transition: '1.4s ease' }
    }
    if (theme === 'mondrian') {
      const m = MONDRIAN_MODULES[module] || MONDRIAN_MODULES.default
      return { background: m.bodyBg }
    }
    if (theme === 'chatrone-dark') return { background: '#17222E' }
    if (theme.startsWith('chatrone')) return { background: '#F4F6F8' }
    return {}
  })()

  // Tenth Gate §E: North Star is the Chatrone OS desktop shell, not a Home theme.
  // The ns-shell class recesses the shared chrome everywhere — same functionality,
  // quiet paper/ink/hairline character. Legacy theme machinery remains for rollback
  // (§E.4) but no longer drives the default desktop experience.
  return (
    <div className={`h-screen flex flex-col overflow-hidden ns-shell ${shellPreview ? 'bridge-shell' : ''}`} style={{ background: 'var(--ns-paper)' }}>
      <OfflineStatusBanner />
      {updateReady && (
        <div className="flex items-center justify-between px-4 py-1.5 bg-navy-700 text-white text-xs">
          <span>A new version of Chatrone is ready.</span>
          <button
            onClick={applyUpdate}
            className="ml-4 px-3 py-1 rounded bg-white text-navy-800 font-medium hover:bg-warm-100 transition-colors"
          >
            Update now
          </button>
        </div>
      )}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        <Sidebar
          isMobile={false}
          isOpen={true}
          onClose={() => {}}
          collapsed={collapsed}
          onToggleCollapse={toggleCollapse}
        />
        <main
          className={`relative flex-1 min-w-0 flex flex-col overflow-hidden transition-all duration-200 ${collapsed ? 'ml-16' : 'ml-56'}`}
        >
          {/* Theme background layers — positioned absolute, behind page content */}
          <MondrianBg />
          <MotherwellBg />

          {/* Page content — z-10 so it floats above background layers.
              No overflow set here — each page manages its own scroll.
              Document-flow pages (Contacts, Companies, etc.) put overflow-y-auto
              on their own flex-1 content div. Full-height column pages like Inbox
              own their scroll containers per-column. This keeps sticky positioning
              inside Inbox working correctly. */}
          <div className="relative z-10 flex flex-col flex-1 min-h-0">
            {/* Universal row — Dashboard/Inbox/Messages/Tasks/Comms/Calendar,
                always visible from every Block */}
            {/* Open work surfaces — literal tabs, at the VERY TOP like a browser's
                (Aaron 2026-09-07: inside the work area they ate the usable space). */}
            {/* The trailer's guided rail — renders nothing outside demo mode. */}
            <DemoRail />
            <WorkspaceTabStrip />
            {/* Janet's persistent object path (issue #169 Slice 6A) — the
                "← Back to X" bridge when a lateral door took Aaron away
                from where Janet last anchored him. */}
            <JanetReturnBridge />
            <UniversalBar />
            {/* Tenth Gate §E.3: no block-accent band in the North Star shell — the
                hairline under the universal bar is the separation. */}
            {/* Block section tabs — left rail picks the Block, these are its sections */}
            <BlockTabBar skipUniversal />
            {theme === 'rothko' && <div className="module-accent-line mx-4 mt-3" />}
            {/* Per-page boundary: a crash in one page shows a retry card while
                the sidebar/nav stay usable; keyed by path so it clears on nav. */}
            {shellPreview ? (
              <ShellFrame>
                <PaneErrorBoundary label="this page" resetKeys={[location.pathname]}>
                  <Outlet context={{ isMobile: false, toggleSidebar: () => setSidebarOpen(v => !v) }} />
                </PaneErrorBoundary>
              </ShellFrame>
            ) : (
              <PaneErrorBoundary label="this page" resetKeys={[location.pathname]}>
                <Outlet context={{ isMobile: false, toggleSidebar: () => setSidebarOpen(v => !v) }} />
              </PaneErrorBoundary>
            )}
          </div>
        </main>
        <EventNudge />
        {/* The deal page's Story and Terms views (they dock Janet in the inspector) and Home (issue #176 — Janet is the persistent bridge
            interaction surface, docked directly on the page) both dock Janet inline —
            nothing floats over the work on either. */}
        {!shellPreview && location.pathname !== '/' && !(/^\/deals\/[0-9a-f-]{36}$/.test(location.pathname) && /[?&]open=(story|terms)\b/.test(location.search)) && !location.pathname.startsWith('/janet/chat') && <JanetFAB bottomOffset="bottom-24" />}
        {/* Home docks Janet's own chat panel — composer + message list — against
            the right edge at lg+ (`clamp(340px, 26vw, 420px)` wide, see
            HomeNorthStar.jsx), right where a bottom-right FAB otherwise sits.
            `side="left"` isn't a safe alternative here — Sidebar's own
            sign-out control is pinned to ITS bottom edge on every Home width
            (≥768px), so moving left would just relocate the same class of
            defect. Raising the vertical offset alone (2026-09-30, first
            pass) only relocated the overlap from the composer to the message
            list — the dock spans nearly the full column height, so ANY
            fixed bottom-right position inside its (up to 420px) width still
            lands inside it. The actual fix (second pass, same day) moves it
            OUT of the dock's horizontal footprint at lg+: right-6 stays
            below lg, where the compact dock isn't a fixed column and the
            existing bottom-24 clearance already keeps it off the composer
            and the "As of …" footer; at lg+ it steps left of the dock's max
            width (420px, plus margin) and returns to the normal bottom-6,
            since it no longer shares any screen position with the dock at
            all.

            Below lg, the compact dock is a normal-flow element inside the
            scrolling page (not fixed), so no single fixed offset for this
            button clears its EXPANDED panel's Send button at every viewport
            height — the panel's on-screen position moves with scroll/height,
            the button's own offset doesn't (2026-09-30 repair, third pass,
            issue #178: reproduced at 900×800/1023×768/1000×800/900×700,
            clean at 900×1000/768×1024). So below lg the button is hidden
            for as long as the compact dock is expanded (shared via
            JanetPageContext's compactDockExpanded, set by
            HomeJanetDockMobile) rather than guessing an offset that's only
            safe at some heights — it reappears the moment the dock
            collapses. lg+ is untouched: compactDockExpanded is never true
            there (the compact dock is CSS-hidden, untappable, above lg). */}
        <BugReportFAB
          bottomOffset={location.pathname === '/' && !shellPreview ? 'bottom-24 lg:bottom-6' : 'bottom-6'}
          side="right"
          horizontalOffset={location.pathname === '/' && !shellPreview ? 'right-6 lg:right-[440px]' : undefined}
          hiddenBelowLg={(location.pathname === '/' || shellPreview) && compactDockExpanded}
        />
        <InboundCallPopup />
      </div>

      {activeReview && (
        <CallLogReviewModal
          key={activeReview.id}
          callLog={activeReview}
          onConfirmed={handleConfirmed}
          onDismiss={handleDismiss}
        />
      )}
    </div>
  )
}

export default function Layout() {
  const isMobile = useIsMobile()
  const { updateReady, applyUpdate } = useDeploymentRefresh()
  const { user } = useAuth()
  const { profile, updateTheme } = useUserProfile(user?.id)
  // Janet's new items (Today, conversations she starts) → a desktop notification.
  useJanetDesktopNotifications()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  // Responsive rail default (Phase 2 shell, Aaron 2026-08-15): laptop-class
  // widths get the compact icon rail — the labeled blocks compete with the
  // work at 1536px; a large workstation (≥1920 CSS px) can afford them. An
  // explicit user toggle is stored and always wins over the width default.
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const stored = localStorage.getItem('sidebar-collapsed')
      if (stored != null) return stored === 'true'
    } catch { /* fall through to the width default */ }
    return typeof window !== 'undefined' && window.innerWidth < 1920
  })

  useEffect(() => {
    const onResize = () => {
      try { if (localStorage.getItem('sidebar-collapsed') != null) return } catch {}
      setCollapsed(window.innerWidth < 1920)
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const toggleCollapse = () => {
    setCollapsed(prev => {
      const next = !prev
      try { localStorage.setItem('sidebar-collapsed', String(next)) } catch {}
      return next
    })
  }

  if (isMobile) {
    return (
      <MessagesUnreadProvider userId={user?.id}>
        <ThemeProvider initialTheme={profile?.theme || 'blocks'} onThemeChange={updateTheme}>
          <ThemeWrapper>
            <MobileLayout />
          </ThemeWrapper>
        </ThemeProvider>
      </MessagesUnreadProvider>
    )
  }

  return (
    <MessagesUnreadProvider userId={user?.id}>
      <ThemeProvider initialTheme={profile?.theme || 'blocks'} onThemeChange={updateTheme}>
        <ThemeWrapper>
          <DesktopShell
            collapsed={collapsed}
            toggleCollapse={toggleCollapse}
            setSidebarOpen={setSidebarOpen}
            updateReady={updateReady}
            applyUpdate={applyUpdate}
          />
        </ThemeWrapper>
      </ThemeProvider>
    </MessagesUnreadProvider>
  )
}
